import React, { useState } from 'react';
import Navbar from '../../components/layout/Navbar/Navbar';
import Footer from '../../components/layout/Footer/Footer';
import './Legal.css';
import { ChevronDown, Lock, Database, Eye } from 'lucide-react';

const Privacy = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (id) => {
    setExpandedSections(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const sections = [
    {
      id: 1,
      title: "Introduction",
      content: [
        { text: 'Squaay LTD ("We," "Us," "Our," or "Company") is committed to protecting your privacy. This Privacy Policy describes how we collect, use, disclose, and otherwise process your personal data in connection with our services, including our website, mobile applications, and related services (collectively, the "Service").' }
      ]
    },
    {
      id: 2,
      title: "Interpretation and Definitions",
      content: [
        { label: '"Personal Data"', text: 'means any information relating to an identified or identifiable natural person.' },
        { label: '"Processing"', text: 'means any operation performed on personal data, such as collection, recording, organization, use, disclosure, or storage.' },
        { label: '"Data Controller"', text: 'means the natural or legal person who determines the purposes and means of processing personal data.' },
        { label: '"Data Processor"', text: 'means the natural or legal person who processes personal data on behalf of the data controller.' },
        { label: '"Service"', text: 'refers to all services provided by Squaay, including the social media platform, food ordering, delivery, and logistics services.' }
      ]
    },
    {
      id: 3,
      title: "Information We Collect",
      content: [
        { text: 'We may collect the following types of information:' },
        { label: 'Account Information', text: 'Name, email address, phone number, profile picture, delivery address, payment method, and other information you provide when creating an account.' },
        { label: 'Usage Information', text: 'Information about how you interact with our Service, including pages visited, links clicked, search queries, and features used.' },
        { label: 'Device Information', text: 'Device type, operating system, browser type, IP address, unique device identifiers, and mobile network information.' },
        { label: 'Location Information', text: 'We may collect location information from your device to provide location-based services and improve delivery accuracy.' },
        { label: 'Transaction Information', text: 'Details about purchases, payments, refunds, and order history.' },
        { label: 'Communications', text: 'Records of customer service interactions, feedback, reviews, and messages sent through our platform.' }
      ]
    },
    {
      id: 4,
      title: "How We Use Your Information",
      content: [
        { text: 'We use the information we collect for the following purposes:' },
        { text: '• To provide, maintain, and improve the Service.' },
        { text: '• To process transactions and send related information.' },
        { text: '• To respond to your inquiries and provide customer support.' },
        { text: '• To send marketing and promotional communications (with your consent).' },
        { text: '• To analyze usage patterns and improve user experience.' },
        { text: '• To detect, prevent, and address fraud, security, and technical issues.' },
        { text: '• To comply with legal obligations and enforce our Terms of Use.' }
      ]
    },
    {
      id: 5,
      title: "Data Sharing and Disclosure",
      content: [
        { text: 'We may share your information in the following circumstances:' },
        { label: 'Service Providers', text: 'We share information with third-party service providers who assist us in operating the Service, including payment processors, delivery partners, and analytics providers.' },
        { label: 'Business Partners', text: 'We may share information with restaurants, vendors, and other business partners to facilitate transactions.' },
        { label: 'Legal Requirements', text: 'We may disclose information if required by law, regulation, court order, or governmental request.' },
        { label: 'Business Transfers', text: 'In the event of a merger, acquisition, bankruptcy, or sale of assets, your information may be transferred as part of that transaction.' },
        { label: 'Consent', text: 'We may share information with your explicit consent for specific purposes.' }
      ]
    },
    {
      id: 6,
      title: "Data Security",
      content: [
        { text: 'We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is completely secure. Therefore, we cannot guarantee absolute security.' }
      ]
    },
    {
      id: 7,
      title: "Retention of Data",
      content: [
        { text: 'We retain your personal data for as long as necessary to provide the Service and fulfill the purposes outlined in this Privacy Policy. We retain account information for as long as your account is active. Transaction records are retained for legal, tax, and accounting purposes as required by applicable laws.' }
      ]
    },
    {
      id: 8,
      title: "Your Rights",
      content: [
        { text: 'Depending on your location, you may have the following rights regarding your personal data:' },
        { text: '• Right to Access: You have the right to request access to your personal data.' },
        { text: '• Right to Rectification: You have the right to correct inaccurate or incomplete personal data.' },
        { text: '• Right to Erasure: You have the right to request deletion of your personal data, subject to legal obligations.' },
        { text: '• Right to Restrict Processing: You have the right to request restrictions on how your data is processed.' },
        { text: '• Right to Data Portability: You have the right to receive your personal data in a structured, commonly used format.' },
        { text: '• Right to Object: You have the right to object to certain processing activities.' },
        { text: 'To exercise any of these rights, please contact us using the information provided below.' }
      ]
    },
    {
      id: 9,
      title: "Cookies and Tracking Technologies",
      content: [
        { text: 'We use cookies, web beacons, and similar tracking technologies to enhance your experience on our Service. Cookies help us remember your preferences, understand usage patterns, and deliver personalized content. You can control cookie settings through your browser preferences, though disabling cookies may limit certain Service features.' }
      ]
    },
    {
      id: 10,
      title: "Third-Party Links",
      content: [
        { text: 'Our Service may contain links to third-party websites. We are not responsible for the privacy practices of external sites. We encourage you to review the privacy policies of any third-party websites before providing your personal information.' }
      ]
    },
    {
      id: 11,
      title: "Children's Privacy",
      content: [
        { text: 'The Service is not intended for individuals under the age of 18. We do not knowingly collect personal data from children. If we become aware that a child has provided personal data, we will take steps to delete such information and terminate the child\'s account.' }
      ]
    },
    {
      id: 12,
      title: "Changes to This Privacy Policy",
      content: [
        { text: 'We may update this Privacy Policy from time to time. We will notify you of any significant changes by posting the updated policy on our website and updating the "Last Updated" date. Your continued use of the Service constitutes your acceptance of the revised Privacy Policy.' }
      ]
    },
    {
      id: 13,
      title: "Contact Us",
      content: [
        { text: 'If you have questions about this Privacy Policy or our privacy practices, please contact us at:' },
        { text: 'Email: support@squaay.com' },
        { text: 'Phone: +234 (808) 408-2347' }
      ]
    }
  ];

  return (
    <>
      <Navbar />
      <div className="legal-page">
        {/* Hero Section */}
        <section className="legal-hero">
          <div className="legal-hero-background">
            <div className="gradient-blob blob-1"></div>
            <div className="gradient-blob blob-2"></div>
          </div>

          <div className="legal-hero-content">
            <div className="legal-hero-icon">
              <Lock size={48} />
            </div>
            <h1 className="legal-hero-title">Privacy Policy</h1>
            <p className="legal-hero-subtitle">
              We value your privacy and are committed to protecting your personal data
            </p>
            <p className="legal-update-date">
              Last Updated: <span>February 14, 2025</span>
            </p>
          </div>
        </section>

        {/* Privacy Features */}
        <section className="legal-features">
          <div className="legal-container">
            <div className="legal-features-grid">
              <div className="feature-card">
                <Database size={32} />
                <h3>Data Protection</h3>
                <p>Your data is encrypted and secured with industry-standard practices</p>
              </div>
              <div className="feature-card">
                <Eye size={32} />
                <h3>Transparency</h3>
                <p>We clearly explain how we collect and use your information</p>
              </div>
              <div className="feature-card">
                <Lock size={32} />
                <h3>Your Control</h3>
                <p>You have full control over your personal information and choices</p>
              </div>
            </div>
          </div>
        </section>

        {/* Table of Contents */}
        <section className="legal-toc">
          <div className="legal-container">
            <h2>Table of Contents</h2>
            <div className="toc-grid">
              {sections.map((section) => (
                <a key={section.id} href={`#section-${section.id}`} className="toc-item">
                  {section.id}. {section.title}
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Content Sections */}
        <section className="legal-content">
          <div className="legal-container">
            {sections.map((section) => (
              <div key={section.id} id={`section-${section.id}`} className="legal-section">
                <button
                  className={`section-header ${expandedSections[section.id] ? 'expanded' : ''}`}
                  onClick={() => toggleSection(section.id)}
                >
                  <div className="section-title-wrapper">
                    <span className="section-number">{section.id}</span>
                    <h3>{section.title}</h3>
                  </div>
                  <ChevronDown size={20} />
                </button>

                {expandedSections[section.id] && (
                  <div className="section-content">
                    {section.content.map((item, idx) => (
                      <p key={idx} className={item.label ? 'definition-item' : 'content-paragraph'}>
                        {item.label && <strong>{item.label}:</strong>}
                        {item.text}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default Privacy;
